import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  TableSortLabel,
  Alert,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useApiQuery, useApiMutation } from "../../hooks/useApi";
import api from "../../utils/api";
import * as XLSX from "xlsx";
import { FileDownload as FileDownloadIcon } from "@mui/icons-material";

const FEE_STATUS_CONFIG = {
  settled: { color: "success", label: "Settled" },
  pending: { color: "error", label: "Pending" },
};

function SettleFeesPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("updatedAt");
  const [order, setOrder] = useState("desc");

  // Search state — inputValue is what the user types, debouncedSearch is what hits the API
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce: wait 400ms after the user stops typing before updating debouncedSearch
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Dialog states
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [settling, setSettling] = useState(false);
  const [settleError, setSettleError] = useState("");

  // Receipts dialog state
  const [receiptsDialogOpen, setReceiptsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientReceipts, setClientReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);

  // Build the URL — include search param only when there's something to search
  const queryUrl = debouncedSearch
    ? `/jobs/for-settlement?search=${encodeURIComponent(debouncedSearch)}`
    : `/jobs/for-settlement`;

  const {
    data: jobs = [],
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    // Include debouncedSearch in the key so React Query refetches when it changes
    ["jobs-for-settlement", debouncedSearch],
    queryUrl
  );

  const handleExportToExcel = () => {
  // Prepare data for Excel with the specified columns
  const excelData = sortedJobs.map((job) => ({
    "Job ID": job.JobId,
    "Client Name": job.clientName,
    "Phone Number": job.clientPhone,
    "Job Type": job.jobType,
    "Job Fees (₹)": job.fees || 0,
    "Client Balance (₹)": job.balance || 0,
    "Assigned To": job.assignedTo || "N/A",
    "Fee Status": FEE_STATUS_CONFIG[job.feeStatus]?.label || job.feeStatus,
    "Due Amount (₹)": job.dueAmount || 0,
  }));

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Jobs For Settlement");

  // Set column widths for better readability
  const columnWidths = [
    { wch: 12 },  // Job ID
    { wch: 20 },  // Client Name
    { wch: 15 },  // Phone Number
    { wch: 20 },  // Job Type
    { wch: 15 },  // Job Fees
    { wch: 18 },  // Client Balance
    { wch: 20 },  // Assigned To
    { wch: 12 },  // Fee Status
    { wch: 15 },  // Due Amount
  ];
  worksheet["!cols"] = columnWidths;

  // Generate filename with current date
  const fileName = `Jobs_For_Settlement_${new Date().toISOString().split('T')[0]}.xlsx`;

  // Export the file
  XLSX.writeFile(workbook, fileName);
};

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const sortedJobs = useMemo(() => {
    if (!jobs) return [];
    return [...jobs].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === "updatedAt") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (orderBy === "fees" || orderBy === "balance") {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [jobs, orderBy, order]);

  const paginatedJobs = useMemo(
    () => sortedJobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedJobs, page, rowsPerPage]
  );

  const handleChangePage = useCallback((_, newPage) => setPage(newPage), []);
  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  }, []);

  const handleOpenSettleDialog = (job) => {
    setSelectedJob(job);
    setRemarks("");
    setSettleError("");
    setSettleDialogOpen(true);
  };

  const handleCloseSettleDialog = () => {
    setSettleDialogOpen(false);
    setSelectedJob(null);
    setRemarks("");
    setSettleError("");
  };

  const handleOpenReceiptsDialog = async (job) => {
    setSelectedClient({ id: job.clientId, name: job.clientName, phone: job.clientPhone });
    setReceiptsDialogOpen(true);
    setReceiptsLoading(true);
    try {
      const response = await api.get(`/receipts/client/${job.clientId}`);
      setClientReceipts(response.data);
    } catch (err) {
      console.error(err);
      setClientReceipts([]);
    } finally {
      setReceiptsLoading(false);
    }
  };

  const handleCloseReceiptsDialog = () => {
    setReceiptsDialogOpen(false);
    setSelectedClient(null);
    setClientReceipts([]);
  };

  const canSettle = useMemo(() => {
    if (!selectedJob) return false;
    return selectedJob.balance >= selectedJob.fees && selectedJob.fees >= 0;
  }, [selectedJob]);

  const settleMutation = useApiMutation(
    selectedJob ? `/settlements/settle-with-client-balance/${selectedJob.JobId}` : "",
    "POST",
    { invalidateQueries: ["jobs-for-settlement", debouncedSearch] }
  );

  const handleSettleFees = async () => {
    if (!selectedJob || !canSettle) return;
    setSettling(true);
    setSettleError("");
    try {
      await settleMutation.mutateAsync({ remarks });
      handleCloseSettleDialog();
      refetch();
    } catch (err) {
      setSettleError(err?.response?.data?.message || "Failed to settle fees");
    } finally {
      setSettling(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading jobs: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Settle Job Fees
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Process fee settlements using client balance
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Tooltip title="Export all jobs to Excel">
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={sortedJobs.length === 0}
            >
              Export to Excel
            </Button>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by client name, phone number, or Job ID (e.g. FIS-0012)…"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isLoading && debouncedSearch ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon color="action" />
                )}
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setInputValue("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Jobs Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "clientName"}
                    direction={orderBy === "clientName" ? order : "asc"}
                    onClick={() => handleRequestSort("clientName")}
                  >
                    Client
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "jobType"}
                    direction={orderBy === "jobType" ? order : "asc"}
                    onClick={() => handleRequestSort("jobType")}
                  >
                    Job Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "fees"}
                    direction={orderBy === "fees" ? order : "asc"}
                    onClick={() => handleRequestSort("fees")}
                  >
                    Job Fees
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "balance"}
                    direction={orderBy === "balance" ? order : "asc"}
                    onClick={() => handleRequestSort("balance")}
                  >
                    Client Balance
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "feeStatus"}
                    direction={orderBy === "feeStatus" ? order : "asc"}
                    onClick={() => handleRequestSort("feeStatus")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedJobs.map((job) => {
                const canSettleJob = job.balance >= job.fees && job.fees >= 0;
                return (
                  <TableRow key={job.JobId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {job.clientName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {job.clientPhone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.jobType}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {job.JobId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AttachMoneyIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {formatCurrency(job.fees)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccountBalanceIcon
                          fontSize="small"
                          color={canSettleJob ? "success" : "error"}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={canSettleJob ? "success.main" : "error.main"}
                        >
                          {formatCurrency(job.balance)}
                        </Typography>
                      </Stack>
                      {canSettleJob ? (
                        <Typography variant="caption" color="success.main">
                          Sufficient for settlement
                        </Typography>
                      ) : job.fees > 0 ? (
                        <Typography variant="caption" color="error.main">
                          Need {formatCurrency(job.fees - job.balance)} more
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={FEE_STATUS_CONFIG[job.feeStatus]?.label || job.feeStatus}
                        color={FEE_STATUS_CONFIG[job.feeStatus]?.color || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip
                          title={
                            !canSettleJob ? "Insufficient client balance" : "Settle using client balance"
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleOpenSettleDialog(job)}
                              disabled={!canSettleJob}
                            >
                              Settle
                            </Button>
                          </span>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ReceiptIcon />}
                          onClick={() => handleOpenReceiptsDialog(job)}
                        >
                          Receipts
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedJobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {debouncedSearch ? `No jobs found for "${debouncedSearch}"` : "No jobs found for settlement"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {debouncedSearch
                        ? "Try a different client name, phone number, or Job ID."
                        : "All jobs have been settled."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedJobs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      {/* Settle Dialog */}
      <Dialog open={settleDialogOpen} onClose={handleCloseSettleDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Settle Job Fees Using Client Balance
            </Typography>
            <IconButton onClick={handleCloseSettleDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box>
              <Paper sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Job Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Job ID:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedJob.JobId}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Client Name:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedJob.clientName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Phone:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedJob.clientPhone}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Job Type:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedJob.jobType}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Settlement Breakdown
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" color="textSecondary">Job Fees:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {formatCurrency(selectedJob.fees)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" color="textSecondary">Client Current Balance:</Typography>
                    <Typography variant="h6" fontWeight="bold" color={canSettle ? "success.main" : "error.main"}>
                      {formatCurrency(selectedJob.balance)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" fontWeight={600}>After Settlement Balance:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="info.main">
                      {formatCurrency(selectedJob.balance - selectedJob.fees)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <TextField
                fullWidth
                label="Remarks (Optional)"
                multiline
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add any notes or remarks about this settlement..."
                sx={{ mb: 2 }}
              />

              {settleError && <Alert severity="error" sx={{ mb: 2 }}>{settleError}</Alert>}
              {!canSettle && selectedJob.fees > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Cannot settle: Client needs {formatCurrency(selectedJob.fees - selectedJob.balance)} more.
                </Alert>
              )}
              {selectedJob.fees <= 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>No fees charged — settlement not required.</Alert>
              )}
              {canSettle && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Settlement can proceed. {formatCurrency(selectedJob.fees)} will be deducted.
                  Remaining balance: {formatCurrency(selectedJob.balance - selectedJob.fees)}.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseSettleDialog} variant="outlined" disabled={settling}>
            Cancel
          </Button>
          <Button
            onClick={handleSettleFees}
            variant="contained"
            color="success"
            startIcon={settling ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            disabled={settling || !canSettle}
          >
            {settling ? "Settling..." : "Confirm Settlement"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Receipts Dialog */}
      <Dialog open={receiptsDialogOpen} onClose={handleCloseReceiptsDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Client Receipts — {selectedClient?.name}
            </Typography>
            <IconButton onClick={handleCloseReceiptsDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box>
              <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Client ID:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedClient.id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Phone:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedClient.phone}</Typography>
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>All Receipts</Typography>
                <Divider sx={{ mb: 2 }} />
                {receiptsLoading ? (
                  <Box display="flex" justifyContent="center" py={5}>
                    <CircularProgress size={40} />
                  </Box>
                ) : clientReceipts.length === 0 ? (
                  <Box textAlign="center" py={6}>
                    <ReceiptIcon sx={{ fontSize: 60, color: "grey.400" }} />
                    <Typography variant="h6" color="textSecondary" mt={2}>No receipts found</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Receipt No.</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Payment Method</TableCell>
                          <TableCell>Reference / Bank</TableCell>
                          <TableCell>Collected Date</TableCell>
                          <TableCell>Collected By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientReceipts.map((r) => (
                          <TableRow key={r._id} hover>
                            <TableCell><Typography fontWeight={600}>{r.receiptNumber}</Typography></TableCell>
                            <TableCell>
                              <Typography fontWeight={600} color="primary.main">
                                {formatCurrency(r.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={r.paymentMethod.replace("_", " ")} color="info" size="small" />
                            </TableCell>
                            <TableCell>
                              {r.transactionReference ? `TXN: ${r.transactionReference}` : r.bank ? `Bank: ${r.bank}` : "—"}
                            </TableCell>
                            <TableCell>
                              {new Date(r.collectedDate).toLocaleString("en-IN", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </TableCell>
                            <TableCell>{r.collectedBy?.name || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={handleCloseReceiptsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SettleFeesPage;